// The page↔extension broadcast bus — `woo-site-message.ts` and
// `broadcast-channel.service.ng1.ts`, as a contract both sides can import.
//
// ⚠️ THIS IS A THIRD CHANNEL, NOT A VARIANT OF THE OTHER TWO. Channel 1 is
// `chrome.runtime` messaging between the extension's own contexts; channel 2 is the
// `postMessage` request/response the SPA and the content script use (`iframe-client.ts`).
// This one is a FAN-OUT with no reply: one surface says "the signed-in user changed" and
// every other surface hears it, including surfaces on origins the sender cannot name.
//
// It exists because the obvious mechanism cannot reach far enough. A native
// `BroadcastChannel` is same-origin, so an iframe and a tab on `extension.dev.woo.io` can already
// talk — but the POPUP runs on `chrome-extension://…`, and nothing same-origin reaches it.
// Routing through the worker is what crosses that boundary, and it is the only reason the
// second implementation exists.

/** Events the PAGE dispatches, which the content script listens for. */
export const WooSiteMessageType = {
  WooBroadcastMessage: 'woo-broadcast-message',
  WooGetStorageMessage: 'woo-get-storage-message',
  WooSetStorageMessage: 'woo-set-storage-message',
  WooRemoveStorageMessage: 'woo-remove-storage-message',
} as const
export type WooSiteMessageType = (typeof WooSiteMessageType)[keyof typeof WooSiteMessageType]

/** Messages the content script posts back INTO the page, via `window.postMessage`. */
export const WooSiteMessageResponseType = {
  PostWooBroadcastMessage: 'post-woo-broadcast-message',
  PostWooGetStorageMessage: 'post-woo-get-storage-message',
} as const
export type WooSiteMessageResponseType =
  (typeof WooSiteMessageResponseType)[keyof typeof WooSiteMessageResponseType]

/**
 * What travels: a topic and an opaque payload.
 *
 * ⚠️ `uniqueId` IS NOT BOOKKEEPING. Every surface stamps its own id and ignores messages
 * carrying it, because the fan-out goes to EVERY tab including the one that sent it. Without
 * it a sign-out echoes back to the surface that raised it and the two bounce.
 */
export interface BroadcastEnvelope<T = unknown> {
  topic: string
  data: T & { uniqueId?: number }
}

/** The page-side global the content script installs. Absent means no extension is listening. */
export interface WooBroadcastGlobal {
  wooBroadcastMessage?: (envelope: BroadcastEnvelope) => void
  /** Upstream sets `'1.0.0'`; it is what a page can feature-detect on. */
  wooCompatibilityVersion?: string
}

// The STORAGE half of the same bridge — `woo-site-message.ts`'s other three globals. It lets
// a woo.io page keep values in `chrome.storage.local`, which unlike the page's own
// `localStorage` is shared by every origin the SPA is framed into: upstream's
// `ExtensionStorageServiceLoaded` uses it to read what the recruiter left on OTHER hosts.
// Same three worlds, same two hops as the broadcast; only `get` has a way back.

export interface SetStorageMessage {
  key: string
  value: string
}

export interface RemoveStorageMessage {
  key: string
}

/**
 * ⚠️ `requestId` IS THE ONLY THING THAT PAIRS A REPLY WITH ITS REQUEST. Every answer arrives
 * as a `window` message of the same type, and a page can have several reads in flight — one
 * per other host — so a reader that took the first reply would take someone else's value.
 */
export interface GetStorageMessage {
  key: string
  requestId: string
}

/** What `PostWooGetStorageMessage` carries back. `value` is absent when nothing is stored. */
export interface GetStorageReply {
  requestId: string
  value?: string
}

export interface WooSiteStorageGlobal {
  wooSetStorageMessage?: (key: string, value: string) => void
  wooRemoveStorageMessage?: (key: string) => void
  wooGetStorageMessage?: (key: string, requestId: string) => void
}

/**
 * Whether the extension-routed implementation is available IN THIS PAGE.
 *
 * ⚠️ IT IS A GLOBAL CHECK, NOT A ROUND TRIP, and that is deliberate upstream
 * (`shouldUseNewImplementation()` is `!!window.wooBroadcastMessage`). Asking the extension
 * over `chrome.runtime` would be async, and the decision has to be made synchronously at the
 * moment a message is posted — the content script may still be loading when a channel is
 * created, so the choice is deferred to each send rather than fixed at construction.
 */
export function extensionBroadcastAvailable(view: WooBroadcastGlobal = globalThis as WooBroadcastGlobal): boolean {
  return typeof view.wooBroadcastMessage === 'function'
}
